import React, { useState, useRef, useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import authService from '../services/auth';
import config from '../config';
// import markerIcon from '../assets/images/nody_pin.svg'; // RIMOSSO - ora generato dinamicamente
import 'leaflet/dist/leaflet.css';
import { getOptimizedPoiImage } from '../utils/imageUtils';

const DEFAULT_CENTER = {
  lat: 41.9028, // Centro Italia (Roma)
  lng: 12.4964
};

const PADDING = 24;

const Icon = ({ name, className }) => (
  <span className={`material-symbols-outlined ${className || ''}`}>{name}</span>
);

const MapPanel = ({ isOpen, onClose, poi }) => {
  const userLocationRef = useRef(null);
  const [userLocationState, setUserLocationState] = useState(null);
  const [taxonomyData, setTaxonomyData] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const mapRef = useRef(null);
  const watchIdRef = useRef(null);
  const [selectedLang] = useState(localStorage.getItem('language') || config.languages.default);

  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) return null;

    const handleSuccess = (position) => {
      const { latitude, longitude } = position.coords;
      if (isFinite(latitude) && isFinite(longitude)) {
        userLocationRef.current = { latitude, longitude };
        setUserLocationState({ latitude, longitude });
      }
    };

    const handleError = (error) => console.error('Errore geolocalizzazione:', error);

    watchIdRef.current = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      { 
        enableHighAccuracy: true, 
        timeout: 15000, 
        maximumAge: 30000 
      }
    );
  }, []);

  const loadTaxonomyData = async () => {
    if (!poi?.field_tipologia_poi?.[0]?.target_id) return;
    try {
      const response = await authService.get(
        config.API_ENDPOINTS.getTaxonomyTerm(poi.field_tipologia_poi[0].target_id, selectedLang)
      );
      setTaxonomyData(response);
    } catch (error) {
      console.error('Errore nel caricamento della tassonomia:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      getUserLocation();
      loadTaxonomyData();
      return () => {
        if (watchIdRef.current) {
          navigator.geolocation.clearWatch(watchIdRef.current);
        }
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, getUserLocation]);

  useEffect(() => {
    if (isOpen && poi?.field_taxonomy?.tid) {
      loadTaxonomyData();
    }
  }, [isOpen, poi?.field_taxonomy?.tid, loadTaxonomyData]);

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsAnimating(false);
      onClose();
      setTimeout(() => {
        if (mapRef.current && poi?.field_geolocalizzazione?.[0]) {
          const { lat, lng } = poi.field_geolocalizzazione[0];
          mapRef.current.setView([lat, lng], 15);
        }
      }, 1000);
    }, 300);
  };

  const createPoiIcon = () => {
    const color = taxonomyData?.field_colore?.[0]?.color || '#1E88E5';
    const poiImage = getOptimizedPoiImage(poi, 'thumbnail', null); // Fallback gestito internamente
    const uniqueId = `poi-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 719.4 842">
        <defs>
          <pattern id="imagePattern-${uniqueId}" patternUnits="userSpaceOnUse" width="634.4" height="634.4" x="45.5" y="43.5">
            <image x="30" y="-50" width="80%" height="80%" href="${poiImage}" preserveAspectRatio="xMidYMid slice"/>
          </pattern>
        </defs>
        <circle cx="362.7" cy="360.7" r="317.2" fill="url(#imagePattern-${uniqueId})"/>
        <circle cx="362.7" cy="360.7" r="317.2" fill="none" stroke="${color}" stroke-width="60"/>
        <polygon points="359.7,842 436.1,696.4 283.2,696.4" fill="${color}"/>
      </svg>`;

    return L.divIcon({
      html: svgString,
      iconSize: [50, 60],
      iconAnchor: [25, 60],
      className: 'map-marker'
    });
  };

  const createUserIcon = () => {
    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 719.4 842">
        <circle cx="362.7" cy="360.7" r="317.2" fill="#FF5555"/>
        <circle cx="362.7" cy="360.7" r="317.2" fill="none" stroke="#FF3333" stroke-width="60"/>
        <polygon points="359.7,842 436.1,696.4 283.2,696.4" fill="#FF3333"/>
        <path d="M362.7 260 q-25 0-42.5-17.5 t-17.5-42.5 q0-25 17.5-42.5 t42.5-17.5 q25 0 42.5 17.5 t17.5 42.5 q0 25-17.5 42.5 t-42.5 17.5 Z" fill="#FFFFFF"/>
        <path d="M302.7 550 v-140 h-40 v-120 q0-25 17.5-42.5 t42.5-17.5 h80 q25 0 42.5 17.5 t17.5 42.5 v120 h-40 v140 Z" fill="#FFFFFF" transform="translate(0, 40)"/>
      </svg>`;

    return L.divIcon({
      html: svgString,
      iconSize: [50, 60],
      iconAnchor: [25, 60],
      className: 'map-marker user-location-marker'
    });
  };

  const MapController = ({ poi, userLocationRef, userLocationState, PADDING = 24 }) => {
    const map = useMap();
   
    // Commentiamo completamente questo useEffect per rimuovere ogni reset automatico
    /*useEffect(() => {
      if (!map || !poi?.field_geolocalizzazione?.[0] || initialViewSetRef.current) return;
       
      const { lat, lng } = poi.field_geolocalizzazione[0];
      map.setView([lat, lng], 15, { animate: false });
      initialViewSetRef.current = true;
    }, [map, poi]);*/
   
    const showBothLocations = useCallback(() => {
      if (!poi?.field_geolocalizzazione?.[0] || !userLocationRef?.current || !map) {
        // console.log('Mancano dati necessari:', {
        //   hasPoi: !!poi?.field_geolocalizzazione?.[0],
        //   hasUserLocation: !!userLocationRef?.current,
        //   hasMap: !!map
        // });
        return;
      }
       
      try {
        const bounds = L.latLngBounds(
          [poi.field_geolocalizzazione[0].lat, poi.field_geolocalizzazione[0].lng],
          [userLocationRef.current.latitude, userLocationRef.current.longitude]
        ).pad(0.2);
   
        map.fitBounds(bounds, {
          animate: true,
          padding: [PADDING, PADDING],
          maxZoom: 16
        });
      } catch (error) {
        console.error('Errore durante il fit bounds:', error);
      }
    }, [poi, map, userLocationRef, PADDING]);
   
    return userLocationState ? (
      <button
        onClick={() => {
          // console.log('My location button clicked');
          showBothLocations();
        }}
        aria-label="Visualizza posizione attuale e destinazione"
        className="btn bg-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm opacity-75"
        style={{
          position: 'absolute',
          top: '10px',
          right: '8px',
          zIndex: 1000,
          width: '40px',
          height: '40px'
        }}
      >
        <Icon name="my_location" />
      </button>
    ) : null;
  };

  const poiLocation = poi?.field_geolocalizzazione?.[0];

  return (
    <div 
      className={`focus-panel ${isOpen ? 'is-open' : ''} ${isAnimating ? 'is-closing' : ''}`}
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        zIndex: 1050,
        overflow: 'hidden'
      }}
    >
      <div className="sticky-top bg-white border-bottom">
        <div className="container d-flex justify-content-between align-items-center py-3">
          <button
            onClick={handleClose}
            aria-label="Chiudi"
            className="btn bg-white d-flex align-items-center justify-content-center ratio-1x1 goback"
            style={{ position: 'relative', top: 0, left: 0 }}
          >
            <Icon name="close" />
          </button>
          <div className="d-flex align-items-center">
            <Icon name="pin_drop" className="me-2" />
            
          </div>
        </div>
      </div>

      <div className="map-container" style={{ height: 'calc(100dvh - 72px)' }}>
        {poiLocation && (
          <MapContainer
            center={poiLocation ? [poiLocation.lat, poiLocation.lng] : [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng]}
            zoom={poiLocation ? 16 : 6}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
            dragging={true}
            touchZoom={true}
            doubleClickZoom={true}
            scrollWheelZoom={true}
            boxZoom={true}
            keyboard={true}
            key={isOpen ? 'open' : 'closed'}
          >
            <MapController 
              poi={poi}
              userLocationRef={userLocationRef}
              userLocationState={userLocationState}
              PADDING={PADDING}
            />
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            
            <Marker
              position={[poiLocation.lat, poiLocation.lng]}
              icon={createPoiIcon()}
              interactive={false}
            />

            {userLocationState && (
              <Marker
                position={[userLocationState.latitude, userLocationState.longitude]}
                icon={createUserIcon()}
                interactive={false}
              />
            )}
          </MapContainer>
        )}
      </div>
    </div>
  );
};

export default MapPanel;